import { matchQuery } from '@tanstack/react-query';
import { Case } from '@/api/apis/case';
import { queryClient } from '@/queries';
import { caseQueryKeys } from '@/queries/case';
import { LogisticsProjectionResponse, RouteProjectionType } from '@/api/apis/route';
import { routePlannerQueryKeys } from '@/queries/route-planner/route-planner.keys';
import { getRouteLegsDivided } from '@/queries/route-planner/utils';
import { RouteActionsType, SurgeonTrip } from '@/api/apis/mission-planner';
import { convertAutosuggestRoute, MultiSuggestRoutePlanType } from '@/utils/route/chains';
import { multiSuggestKeys, multiSuggestQueryClient } from '@/queries/multi-suggest';
import { MultiSuggestChain } from '@/api/apis/multi-suggest';
import { DataCacheType } from '@/queries/consts';

export const seedCasesInCache = (caseList: Case[]) => {
  caseList.forEach((caseItem) => {
    multiSuggestQueryClient.setQueryData(caseQueryKeys.getItem(caseItem.caseId), caseItem);
  });
};

export const seedLogisticsRoute = (
  logisticsItem: LogisticsProjectionResponse,
  type: DataCacheType = DataCacheType.Live,
) => {
  const queryClientInstance = type === DataCacheType.Live ? queryClient : multiSuggestQueryClient;

  logisticsItem.routes.forEach((route) => {
    queryClientInstance.setQueryData(routePlannerQueryKeys.getRouteByType(logisticsItem.id, route.routeType), {
      ...route,
      ...getRouteLegsDivided(route.legs, logisticsItem.locations),
      caseId: logisticsItem.caseId,
    } as RouteProjectionType);
  });
};

export const seedLogisticsInCache = (logisticsList: LogisticsProjectionResponse[]) => {
  logisticsList.forEach((logisticsItem) => {
    multiSuggestQueryClient.setQueryData(routePlannerQueryKeys.getLogistics(logisticsItem.id), logisticsItem);
    seedLogisticsRoute(logisticsItem, DataCacheType.Snapshot);
  });
};

export const seedEmptySuggestions = (taskId: string, caseIds: string[]) => {
  caseIds.forEach((caseId) => {
    multiSuggestQueryClient.setQueryData(multiSuggestKeys.getMultiSuggestCaseResultByCase(taskId, 0, caseId), null);
  });
};

export const seedMultiSuggestItems = async (
  taskId: string,
  multiSuggestChains: MultiSuggestChain[],
  surgeonChains: SurgeonTrip[][],
  commercialFlightChains: RouteActionsType[][],
  caseIds: string[],
) => {
  if (multiSuggestChains.length) {
    await Promise.all(
      multiSuggestChains.flatMap(async (chain, chainIndex) => {
        const surgeonPlans = surgeonChains[chainIndex] || [];
        const commercialFlightPlans = commercialFlightChains[chainIndex] || [];

        return chain.items.map(async (item, itemIndex) => {
          const route = await convertAutosuggestRoute({
            mainTrip: item,
            surgeonTrip: surgeonPlans[itemIndex],
            commercialTrip: commercialFlightPlans[itemIndex],
          });

          multiSuggestQueryClient.setQueryData<MultiSuggestRoutePlanType | null>(
            multiSuggestKeys.getMultiSuggestCaseResultByCase(taskId, chainIndex, item.caseId),
            route,
          );
        });
      }),
    );
  } else {
    seedEmptySuggestions(taskId, caseIds);
  }
};

export const removeMultiSuggestItemCache = (taskId: string, caseIds: string[], logisticsIds: string[]) => {
  if (!taskId) return;

  multiSuggestQueryClient.removeQueries({
    predicate: (query) => {
      const isMultiSuggestItemQuery = matchQuery(
        { queryKey: multiSuggestKeys.getMultiSuggestResultItem(taskId), exact: false },
        query,
      );

      const isCaseItemQuery = caseIds.some((caseId) => {
        return matchQuery({ queryKey: caseQueryKeys.getItem(caseId), exact: true }, query);
      });

      const isLogisticsItemQuery = logisticsIds.some((logisticsId) => {
        return matchQuery({ queryKey: routePlannerQueryKeys.getLogistics(logisticsId), exact: false }, query);
      });

      return isMultiSuggestItemQuery || isCaseItemQuery || isLogisticsItemQuery;
    },
  });
};
